import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  Play, Pause, SkipBack, SkipForward, 
  RotateCcw, ChevronLeft, Loader2,
  Gauge, BookOpen
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import ChunkAnalyzer from "@/components/ChunkAnalyzer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Timing constants
const BASE_WORD_DURATION_MS = 600;
const BASE_LINE_PAUSE_MS = 1000;

// Animation configs (extracted to prevent re-renders)
const lineAnimationConfig = {
  initial: { opacity: 0, y: 20 },
  transition: { duration: 0.3 }
};

export default function KaraokePlayer({ lyricsData }) {
  const navigate = useNavigate();
  const [currentLine, setCurrentLine] = useState(0);
  const [currentWord, setCurrentWord] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [wordExplanation, setWordExplanation] = useState(null);
  const [loadingWord, setLoadingWord] = useState(null);
  const [openPopover, setOpenPopover] = useState(null);
  const [explanationCache, setExplanationCache] = useState({});
  const [showChunkAnalyzer, setShowChunkAnalyzer] = useState(false);
  
  const intervalRef = useRef(null);
  const scrollRef = useRef(null);

  const lines = lyricsData?.lines || [];
  const songMetadata = lyricsData?.song || null;

  // Redirect if no lyrics data
  useEffect(() => {
    if (!lyricsData || !lyricsData.lines || lyricsData.lines.length === 0) {
      toast.error("No lyrics loaded. Please paste lyrics first.");
      navigate("/");
    }
  }, [lyricsData, navigate]);

  // Word timing (ms per word, adjusted by speed)
  const wordDuration = BASE_WORD_DURATION_MS / speed;
  const linePauseDuration = BASE_LINE_PAUSE_MS / speed;

  const advanceWord = useCallback(() => {
    if (!lines.length) return;
    
    const currentLineWords = lines[currentLine]?.original.split(/\s+/) || [];
    
    if (currentWord < currentLineWords.length - 1) {
      setCurrentWord(prev => prev + 1);
    } else if (currentLine < lines.length - 1) {
      setCurrentLine(prev => prev + 1);
      setCurrentWord(0);
    } else {
      setIsPlaying(false);
    }
  }, [currentLine, currentWord, lines]);

  // Playback loop
  useEffect(() => {
    if (isPlaying && lines.length) {
      const currentLineWords = lines[currentLine]?.original.split(/\s+/) || [];
      const isLastWordOfLine = currentWord >= currentLineWords.length - 1;
      const duration = isLastWordOfLine ? linePauseDuration : wordDuration;
      
      intervalRef.current = setTimeout(advanceWord, duration);
    }
    
    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [isPlaying, currentWord, currentLine, advanceWord, wordDuration, linePauseDuration, lines]);

  // Auto-scroll to current line
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement) {
      const lineElements = scrollElement.querySelectorAll('[data-line-index]');
      const activeElement = lineElements[currentLine];
      if (activeElement) {
        activeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentLine]);

  const handlePlayPause = useCallback(() => {
    setIsPlaying(prev => !prev);
  }, []);

  const handleRestart = useCallback(() => {
    setCurrentLine(0);
    setCurrentWord(0);
    setIsPlaying(false);
  }, []);

  const handlePrevLine = useCallback(() => {
    setCurrentLine(prev => prev > 0 ? prev - 1 : prev);
    setCurrentWord(0);
  }, []);

  const handleNextLine = useCallback(() => {
    setCurrentLine(prev => prev < lines.length - 1 ? prev + 1 : prev);
    setCurrentWord(0);
  }, [lines.length]);

  const handleWordClick = useCallback(async (word, lineIndex, lineOriginal) => {
    const cleanWord = word.replace(/[^\wáéíóúñü]/gi, '');
    if (!cleanWord) return;

    setIsPlaying(false);
    const popoverId = `${lineIndex}-${word}`;
    const cacheKey = `${cleanWord.toLowerCase()}-${lineIndex}`;
    
    // Check cache first
    if (explanationCache[cacheKey]) {
      setWordExplanation(explanationCache[cacheKey]);
      setOpenPopover(popoverId);
      return;
    }
    
    setLoadingWord(popoverId);
    setOpenPopover(popoverId);
    
    try {
      const response = await axios.post(`${API}/lyrics/explain-word`, {
        word: cleanWord,
        context: lineOriginal,
        song_title: songMetadata?.title || null,
        song_artist: songMetadata?.artist || null,
        song_confidence: songMetadata?.confidence || "low",
        song_region: songMetadata?.region || null,
        source_language: "Spanish"
      });
      
      // Cache the result
      setExplanationCache(prev => ({
        ...prev,
        [cacheKey]: response.data
      }));
      setWordExplanation(response.data);
    } catch (err) {
      toast.error("Couldn't get word explanation");
      setWordExplanation(null);
    } finally {
      setLoadingWord(null);
    }
  }, [songMetadata, explanationCache]);

  if (!lines.length) {
    return null;
  }

  // Get full lyrics text for chunk analyzer
  const fullLyricsText = lines.map(l => l.original).join('\n');

  return (
    <div className="min-h-screen flex flex-col bg-[var(--bg-default)]">
      <PlayerHeader 
        navigate={navigate} 
        onLearnClick={() => setShowChunkAnalyzer(true)}
      />
      
      <main className="flex-1 overflow-hidden relative">
        <ScrollArea className="h-[calc(100vh-200px)]" ref={scrollRef}>
          <div className="container mx-auto max-w-4xl px-4 py-12">
            <AnimatePresence>
              {lines.map((line, lineIndex) => (
                <LyricLine
                  key={`${lineIndex}-${line.original.substring(0, 20)}`}
                  line={line}
                  lineIndex={lineIndex}
                  currentLine={currentLine}
                  currentWord={currentWord}
                  isPlaying={isPlaying}
                  onWordClick={handleWordClick}
                  loadingWord={loadingWord}
                  wordExplanation={wordExplanation}
                  openPopover={openPopover}
                  setOpenPopover={setOpenPopover}
                />
              ))}
            </AnimatePresence>
          </div>
        </ScrollArea>
      </main>

      <PlaybackControls
        currentLine={currentLine}
        totalLines={lines.length}
        isPlaying={isPlaying}
        speed={speed}
        onPlayPause={handlePlayPause}
        onRestart={handleRestart}
        onPrevLine={handlePrevLine}
        onNextLine={handleNextLine}
        onSpeedChange={setSpeed}
      />

      {/* Chunk Analyzer Modal */}
      {showChunkAnalyzer && (
        <ChunkAnalyzer 
          lyrics={fullLyricsText}
          onClose={() => setShowChunkAnalyzer(false)}
        />
      )}
    </div>
  );
}

function PlayerHeader({ navigate, onLearnClick }) {
  return (
    <header className="py-4 px-4 md:px-8 border-b border-gray-100 bg-white/80 backdrop-blur-xl">
      <div className="container mx-auto max-w-7xl flex items-center justify-between">
        <Button
          data-testid="back-to-home-btn"
          variant="ghost"
          onClick={() => navigate("/")}
          className="flex items-center gap-2 hover:bg-gray-100 rounded-full"
        >
          <ChevronLeft className="w-5 h-5" />
          <span>Back</span>
        </Button>
        
        <div className="flex items-center gap-2">
          <span className="text-2xl">🎤</span>
          <span className="font-bold text-xl tracking-tight">
            <span className="text-[var(--brand-primary)]">Slang</span>
            <span className="text-[var(--fg-default)]">Decoder</span>
          </span>
        </div>
        
        <Button
          data-testid="learn-chunks-btn"
          variant="outline"
          onClick={onLearnClick}
          className="flex items-center gap-2 rounded-full border-2 border-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/10"
        >
          <BookOpen className="w-4 h-4" />
          <span className="hidden sm:inline">Learn</span>
        </Button>
      </div>
    </header>
  );
}

function PlaybackControls({ 
  currentLine, 
  totalLines, 
  isPlaying, 
  speed, 
  onPlayPause, 
  onRestart, 
  onPrevLine, 
  onNextLine, 
  onSpeedChange 
}) {
  const speedValue = useMemo(() => [speed], [speed]);
  
  return (
    <footer className="playback-controls border-t border-gray-200 py-4 px-4 md:px-8 sticky bottom-0">
      <div className="container mx-auto max-w-4xl">
        <div className="flex items-center justify-center gap-2 mb-4 text-sm text-[var(--fg-muted)]">
          <span>Line {currentLine + 1} of {totalLines}</span>
        </div>
        
        <div className="flex items-center justify-center gap-4 mb-4">
          <Button
            data-testid="restart-btn"
            variant="outline"
            size="icon"
            onClick={onRestart}
            className="rounded-full w-10 h-10"
          >
            <RotateCcw className="w-4 h-4" />
          </Button>
          
          <Button
            data-testid="prev-line-btn"
            variant="outline"
            size="icon"
            onClick={onPrevLine}
            disabled={currentLine === 0}
            className="rounded-full w-10 h-10"
          >
            <SkipBack className="w-4 h-4" />
          </Button>
          
          <Button
            data-testid="play-pause-btn"
            onClick={onPlayPause}
            className="rounded-full w-14 h-14 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white shadow-lg"
          >
            {isPlaying ? (
              <Pause className="w-6 h-6" />
            ) : (
              <Play className="w-6 h-6 ml-1" />
            )}
          </Button>
          
          <Button
            data-testid="next-line-btn"
            variant="outline"
            size="icon"
            onClick={onNextLine}
            disabled={currentLine >= totalLines - 1}
            className="rounded-full w-10 h-10"
          >
            <SkipForward className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-2 ml-4 bg-gray-100 rounded-full px-4 py-2">
            <Gauge className="w-4 h-4 text-[var(--fg-muted)]" />
            <Slider
              data-testid="speed-slider"
              value={speedValue}
              onValueChange={(value) => onSpeedChange(value[0])}
              min={0.5}
              max={2}
              step={0.25}
              className="w-24"
            />
            <span className="text-sm font-medium min-w-[3ch]">{speed}x</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function LyricLine({ 
  line, 
  lineIndex, 
  currentLine, 
  currentWord, 
  isPlaying,
  onWordClick,
  loadingWord,
  wordExplanation,
  openPopover,
  setOpenPopover
}) {
  const isActive = lineIndex === currentLine;
  const isPast = lineIndex < currentLine;
  
  const words = line.original.split(/\s+/);
  const notableWords = useMemo(() => 
    line.words?.filter(w => w.is_notable).map(w => w.word.toLowerCase()) || [],
    [line.words]
  );

  // Calculate opacity based on line state
  let opacity = 1;
  if (isPast) opacity = 0.4;
  else if (!isActive) opacity = 0.6;

  // Determine line class based on state
  let lineClass = 'karaoke-line future';
  if (isActive) lineClass = 'karaoke-line active';
  else if (isPast) lineClass = 'karaoke-line past';

  const animateConfig = useMemo(() => ({ 
    opacity,
    y: 0,
    scale: isActive ? 1.02 : 1
  }), [opacity, isActive]);

  return (
    <motion.div
      data-line-index={lineIndex}
      data-testid={`lyric-line-${lineIndex}`}
      {...lineAnimationConfig}
      animate={animateConfig}
      className={`mb-8 text-center transition-all ${lineClass}`}
    >
      <div className={`font-lyrics mb-2 leading-relaxed ${
        isActive ? 'text-4xl md:text-5xl font-black' : 'text-2xl md:text-3xl font-medium'
      }`}>
        {words.map((word, wordIndex) => (
          <WordWithPopover
            key={`${lineIndex}-${wordIndex}-${word}`}
            word={word}
            wordIndex={wordIndex}
            lineIndex={lineIndex}
            lineOriginal={line.original}
            isActive={isActive}
            currentWord={currentWord}
            isPlaying={isPlaying}
            notableWords={notableWords}
            loadingWord={loadingWord}
            wordExplanation={wordExplanation}
            openPopover={openPopover}
            setOpenPopover={setOpenPopover}
            onWordClick={onWordClick}
          />
        ))}
      </div>
      
      <p className={`text-[var(--fg-muted)] ${isActive ? 'text-lg md:text-xl' : 'text-base'}`}>
        {line.translation}
      </p>
    </motion.div>
  );
}

function WordWithPopover({
  word,
  wordIndex,
  lineIndex,
  lineOriginal,
  isActive,
  currentWord,
  isPlaying,
  notableWords,
  loadingWord,
  wordExplanation,
  openPopover,
  setOpenPopover,
  onWordClick
}) {
  const isCurrentWord = isActive && wordIndex === currentWord && isPlaying;
  const isNotable = notableWords.some(nw => 
    word.toLowerCase().replace(/[^\wáéíóúñü]/gi, '').includes(nw)
  );
  const popoverId = `${lineIndex}-${word}`;
  const isLoading = loadingWord === popoverId;
  const isOpen = openPopover === popoverId;

  const wordClassName = `inline-block mr-2 cursor-pointer transition-colors ${
    isNotable ? 'notable-word underline decoration-[var(--slang-underline)] decoration-2 underline-offset-4' : ''
  } ${isCurrentWord ? 'text-[var(--brand-primary)] font-bold' : ''}`;

  return (
    <Popover 
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) setOpenPopover(null);
      }}
    >
      <PopoverTrigger asChild>
        <span
          data-testid={`word-${lineIndex}-${wordIndex}`}
          className={wordClassName}
          onClick={() => onWordClick(word, lineIndex, lineOriginal)}
        >
          {word}
        </span>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-4 bg-white shadow-xl rounded-2xl border-0"
        data-testid={`word-explanation-${lineIndex}-${wordIndex}`}
      >
        <PopoverBody 
          isLoading={isLoading} 
          wordExplanation={wordExplanation} 
        />
      </PopoverContent>
    </Popover>
  );
}

function PopoverBody({ isLoading, wordExplanation }) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loader2 className="w-6 h-6 animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }
  
  if (wordExplanation) {
    return <WordExplanationCard explanation={wordExplanation} />;
  }
  
  return <p className="text-center text-[var(--fg-muted)]">Click to see meaning</p>;
}

function WordExplanationCard({ explanation }) {
  return (
    <div className="space-y-3 max-h-[400px] overflow-y-auto">
      <div className="flex items-start justify-between">
        <div>
          <h4 className="font-bold text-lg text-[var(--fg-default)]">
            {explanation.word}
          </h4>
          {explanation.pronunciation && (
            <p className="text-sm text-[var(--fg-muted)] italic">
              /{explanation.pronunciation}/
            </p>
          )}
        </div>
        {explanation.is_slang && (
          <span className="bg-[var(--slang-highlight)] text-[var(--fg-default)] text-xs font-bold px-2 py-1 rounded-full">
            Slang
          </span>
        )}
      </div>
      
      <p className="text-[var(--fg-default)]">{explanation.meaning}</p>
      
      {explanation.usage_example && (
        <div className="bg-[var(--bg-subtle)] p-3 rounded-xl">
          <span className="text-xs font-semibold text-[var(--fg-muted)]">Example</span>
          <p className="text-sm text-[var(--fg-default)] mt-1">
            &ldquo;{explanation.usage_example}&rdquo;
          </p>
          {explanation.usage_example_translation && (
            <p className="text-sm text-[var(--fg-muted)] italic mt-1">
              → &ldquo;{explanation.usage_example_translation}&rdquo;
            </p>
          )}
        </div>
      )}
      
      {explanation.cultural_note && (
        <div className="border-l-4 border-[var(--brand-accent)] pl-3">
          <p className="text-sm text-[var(--fg-muted)]">
            <span className="font-semibold">Cultural note:</span> {explanation.cultural_note}
          </p>
        </div>
      )}
    </div>
  );
}
